import {Backdrop, Box, Button, Stack, Typography, useTheme} from '@mui/material';
import {ChatKit, StartScreenOption, ThemeOption, useChatKit} from '@openai/chatkit-react';
import {type AssistantLocale, DEFAULT_ASSISTANT_LOCALE, resolveAssistantLocale} from '@perfectpost/perfect-post-common';
import {ConsoleLogger} from 'aws-amplify/utils';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useCrisp} from '../components/CrispProvider';
import {VITE_CHATKIT_ENDPOINT, NODE_ENV, VITE_ENV} from '../config';
import {useDocumentTitle} from '../useDocumentTitle';
import {makeAmplifyFetch} from '../utils/makeAmplifyFetch';

const logger = new ConsoleLogger('ExtensionChatKit');

// Messages types pour la communication avec l'extension
const NS = 'PP_CHATKIT';

// Mode développement vs production
const isDevelopment = NODE_ENV === 'development' || VITE_ENV === 'development';

// Validation simplifiée des origines pour le déploiement en production
const isValidExtensionOrigin = (origin: string): boolean => {
  // OpenAI ChatKit - origine légitime requise pour le fonctionnement
  if (origin === 'https://cdn.platform.openai.com') {
    return true;
  }
  // Firefox WebExtension - UUID variable, validation par regex
  if (origin.startsWith('moz-extension://')) {
    return /^moz-extension:\/\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(origin);
  }

  // Chrome-family (Chrome, Edge Chromium, Brave, Opera): chrome-extension://<id>
  if (origin.startsWith('chrome-extension://')) {
    // Accepter toutes les extensions Chrome valides (32 caractères alphanumériques)
    return /^chrome-extension:\/\/[a-z0-9]{32}$/.test(origin);
  }

  // Edge peut aussi utiliser extension://<id>
  if (origin.startsWith('extension://')) {
    // Accepter toutes les extensions Edge valides
    return /^extension:\/\/[a-z0-9]{32}$/.test(origin);
  }

  // Safari App Extension - UUID format
  if (origin.startsWith('safari-web-extension://')) {
    // Accepter toutes les extensions Safari valides (format UUID, insensible à la casse)
    return /^safari-web-extension:\/\/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(
      origin,
    );
  }

  return false;
};

interface ExtensionMessage {
  ns: typeof NS;
  cmd: 'SET_OPTIONS' | 'SET_THREAD' | 'SET_DRAFT_ID' | 'AUTH_TOKEN_RESULT' | 'SET_USER_INFO';
  options?: unknown;
  threadId?: string | null;
  draftId?: string;
  reqId?: string;
  token?: string | null;
  expiresAt?: number;
  // Nouvelles propriétés pour les infos utilisateur
  userId?: string;
  userLocale?: string;
  darkMode?: boolean;
}

interface EmbedEvent {
  ns: typeof NS;
  type: 'READY' | 'REQUEST_AUTH_TOKEN' | 'CLIENT_ACTION' | 'CLIENT_TOOL' | 'LOG' | 'ERROR';
  reqId?: string;
  payload?: unknown;
  level?: 'info' | 'warn' | 'error';
  args?: unknown[];
  message?: string;
}

export function ExtensionChatKit() {
  const theme = useTheme();
  const {t} = useTranslation();
  const {crisp} = useCrisp();
  useDocumentTitle('Extension');
  const [isReady, setIsReady] = useState(false);
  const [draftId, setDraftId] = useState<string | undefined>();
  const [threadId, setThreadId] = useState<string | undefined>();
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);
  const [showBackdrop, setShowBackdrop] = useState(false);
  // Langue de l'assistant : celle de l'INTERFACE DE L'EXTENSION, reçue par `SET_USER_INFO`.
  // Repli commun avec le serveur tant que l'extension n'a rien envoyé.
  const [userLocale, setUserLocale] = useState<AssistantLocale>(DEFAULT_ASSISTANT_LOCALE);
  const [darkMode, setDarkMode] = useState<boolean>(false); // Défaut en mode clair
  const [errorState, setErrorState] = useState<{
    type: 'invalid_origin' | 'timeout' | 'connection_failed' | null;
    origin?: string;
    details?: string;
  }>({type: null});
  const pendingAuthRequests = useRef(new Map<string, (token: string | null) => void>());
  const readyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fonction pour envoyer des messages à l'extension parent
  const postToParent = useCallback((message: EmbedEvent) => {
    logger.debug('Sending message to parent:', message);
    window.parent.postMessage(message, '*'); // L'extension validera l'origine
  }, []);

  // Fonction pour vérifier si le token est expiré
  const isTokenExpired = useCallback((expiresAt: number | null): boolean => {
    if (!expiresAt) return true;
    // Ajouter une marge de 30 secondes pour éviter les races conditions
    const now = Date.now();
    const margin = 30 * 1000; // 30 secondes en millisecondes
    return now >= expiresAt - margin;
  }, []);

  // Fonction pour demander un token d'auth à l'extension
  const requestAuthToken = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      const reqId = Math.random().toString(36).slice(2);
      pendingAuthRequests.current.set(reqId, resolve);
      postToParent({ns: NS, type: 'REQUEST_AUTH_TOKEN', reqId});
    });
  }, [postToParent]);

  // Fonction pour obtenir un token valide (demande un nouveau si expiré)
  const getValidToken = useCallback(async (): Promise<string | null> => {
    // Si on n'a pas de token ou s'il est expiré, en demander un nouveau
    if (!authToken || isTokenExpired(tokenExpiresAt)) {
      logger.debug('Token expired or missing, requesting new token');
      const newToken = await requestAuthToken();
      return newToken;
    }

    // Le token actuel est encore valide
    return authToken;
  }, [authToken, tokenExpiresAt, isTokenExpired, requestAuthToken]);

  // Gestionnaire des messages de l'extension
  useEffect(() => {
    const handleMessage = (event: MessageEvent<unknown>) => {
      if (typeof event.data !== 'object' || !event.data) return;

      const data = event.data as Record<string, unknown>;

      // Filtrer uniquement les messages de commande de l'extension (avec namespace PP_CHATKIT)
      if (!data.ns || data.ns !== NS || !data.cmd) return;

      // Pour les commandes d'extension : validation stricte de l'origine et de la source
      if (!isValidExtensionOrigin(event.origin)) {
        logger.warn('Invalid extension origin for command:', event.origin);
        setErrorState({
          type: 'invalid_origin',
          origin: event.origin,
          details: isDevelopment
            ? t('chatkit.error.invalidOriginDev', {origin: event.origin})
            : t('chatkit.error.invalidOriginProd'),
        });
        return;
      }

      // Vérifier que le message vient du parent direct pour les commandes
      if (event.source !== window.parent) {
        logger.debug('Command not from parent window');
        return;
      }

      // Validation supplémentaire des commandes attendues
      const validCommands = ['SET_OPTIONS', 'SET_THREAD', 'SET_DRAFT_ID', 'AUTH_TOKEN_RESULT', 'SET_USER_INFO'];
      if (typeof data.cmd !== 'string' || !validCommands.includes(data.cmd)) {
        logger.debug('Invalid or unexpected command:', data.cmd);
        return;
      }

      const message = event.data as ExtensionMessage;
      logger.debug('Received message from parent:', message);

      // Annuler le timeout de connexion si on reçoit un message valide
      if (readyTimeoutRef.current) {
        clearTimeout(readyTimeoutRef.current);
        readyTimeoutRef.current = null;
      }

      // Effacer l'état d'erreur si on reçoit un message valide
      // Utiliser une fonction de mise à jour pour accéder à la valeur actuelle
      setErrorState((currentErrorState) => {
        if (currentErrorState.type) {
          logger.debug('Clearing error state after receiving valid extension message');
          return {type: null};
        }
        return currentErrorState;
      });

      switch (message.cmd) {
        case 'SET_OPTIONS':
          // Les options sont gérées directement par useChatKit
          logger.debug('Options received from parent');
          break;

        case 'SET_THREAD':
          setThreadId(message.threadId || undefined);
          break;

        case 'SET_DRAFT_ID':
          setDraftId(message.draftId);
          break;

        case 'SET_USER_INFO':
          if (message.userLocale !== undefined) {
            const validatedLocale = resolveAssistantLocale(message.userLocale);
            setUserLocale(validatedLocale);
            logger.debug('User locale set from extension:', validatedLocale);
          }
          if (message.darkMode !== undefined) {
            setDarkMode(message.darkMode);
            logger.debug('Dark mode set from extension:', message.darkMode);
          }
          break;

        case 'AUTH_TOKEN_RESULT':
          if (message.reqId) {
            const resolver = pendingAuthRequests.current.get(message.reqId);
            if (resolver) {
              resolver(message.token || null);
              pendingAuthRequests.current.delete(message.reqId);
            }
          }
          setAuthToken(message.token || null);
          setTokenExpiresAt(message.expiresAt || null);
          logger.debug('Token updated, expires at:', message.expiresAt ? new Date(message.expiresAt) : 'unknown');
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [t, isDevelopment]);

  // Signaler que la page est prête et gérer le timeout
  useEffect(() => {
    if (!isReady && !errorState.type) {
      // Démarrer un timeout pour détecter les problèmes de connexion
      readyTimeoutRef.current = setTimeout(() => {
        setErrorState({
          type: 'timeout',
          details: t('chatkit.error.connectionTimeout'),
        });
      }, 5000); // 5 secondes de timeout

      setIsReady(true);
      postToParent({ns: NS, type: 'READY'});
      logger.info('ExtensionChatKit ready, sent READY to parent');
    }

    // Nettoyer le timeout si le composant se démonte
    return () => {
      if (readyTimeoutRef.current) {
        clearTimeout(readyTimeoutRef.current);
        readyTimeoutRef.current = null;
      }
    };
  }, [isReady, postToParent, errorState.type, t]);

  // Gestionnaire des actions ChatKit
  const handleAction = useCallback(
    async (action: {type: string; payload?: Record<string, unknown>}) => {
      // Gérer l'ouverture du support Crisp directement
      if (action.type === 'open_support_chat') {
        logger.info('Opening Crisp chat directly');
        const supportMessage = t('chatkit.supportMessage');
        crisp.openChat(supportMessage);
        return Promise.resolve();
      }
      // Pour les autres actions, les transmettre à l'extension
      postToParent({ns: NS, type: 'CLIENT_ACTION', payload: action});
      return Promise.resolve();
    },
    [crisp, postToParent],
  );

  // Gestionnaire des tools ChatKit
  const handleClientTool = useCallback(
    (call: {name: string; params: Record<string, unknown>}) => {
      postToParent({ns: NS, type: 'CLIENT_TOOL', payload: {name: call.name, params: call.params}});
      return {};
    },
    [postToParent],
  );

  // Gestionnaire de changement de thread
  const handleThreadChange = useCallback(({threadId}: {threadId: string | null}) => {
    setThreadId(threadId || undefined);
  }, []);

  // Gestionnaire pour afficher/masquer le backdrop
  const handleBackdropToggle = useCallback(() => {
    setShowBackdrop(!showBackdrop);
  }, [showBackdrop]);

  // Gestionnaire pour fermer le backdrop
  const handleBackdropClose = useCallback(() => {
    setShowBackdrop(false);
  }, []);

  // Gestionnaire pour ouvrir le support Crisp
  const handleContactSupport = useCallback(() => {
    crisp.openChat();
    setShowBackdrop(false);
  }, [crisp]);

  // Refs de « dernière valeur connue » : `apiConfig` est mémoïsé avec des dépendances VIDES
  // pour que son identité ne change jamais, sans quoi l'apparition du `draftId` en pleine
  // conversation recréerait la config `api` de `useChatKit` et risquerait de réinitialiser
  // le widget.
  const draftIdRef = useRef(draftId);
  draftIdRef.current = draftId;
  // `getValidToken` change d'identité à chaque rotation de token : sans ref, les
  // dépendances vides figeraient une closure sur un `authToken` périmé.
  const getValidTokenRef = useRef(getValidToken);
  getValidTokenRef.current = getValidToken;
  // `SET_USER_INFO` arrive APRÈS le montage : sans ref, la config `api` — mémoïsée à
  // dépendances vides — figerait le repli et n'enverrait jamais la vraie langue.
  const userLocaleRef = useRef(userLocale);
  userLocaleRef.current = userLocale;

  // Configuration du fetch personnalisé pour l'authentification
  const apiConfig = useMemo(
    () => ({
      url: VITE_CHATKIT_ENDPOINT,
      fetch: makeAmplifyFetch({
        apiName: 'chatkit',
        getDraftId: () => draftIdRef.current,
        // Seul canal qui porte la langue jusqu'au serveur (cf. `getLocale`).
        getLocale: () => userLocaleRef.current,
        tokenProvider: () => getValidTokenRef.current(),
      }),
      domainKey: 'domain_pk_68ecfda6ce8c8190a97e20f2c350ab2a0696ebaba9411d65',
    }),
    [],
  );

  const themeConfig: ThemeOption = useMemo(
    () => ({
      colorScheme: darkMode ? 'dark' : 'light',
      color: {
        accent: {
          primary: '#0a66c2',
          level: 3,
        },
        surface: {
          background: darkMode ? '#1a1a1a' : '#e0ebee',
          foreground: darkMode ? '#2d2d2d' : '#ffffff',
        },
      },
      typography: {fontFamily: theme.typography.fontFamily},
    }),
    [theme.typography.fontFamily, darkMode],
  );

  const startScreen: StartScreenOption = useMemo(
    () => ({
      greeting: t('chatkit.welcome'),
      prompts: [
        {
          label: t('chatkit.writepost'),
          prompt: t('chatkit.writepost'),
          icon: 'write',
        },
        {
          label: t('chatkit.reviewpost'),
          prompt: t('chatkit.reviewpost'),
          icon: 'bolt',
        },
      ],
    }),
    [t],
  );

  // Utiliser threadId directement comme initialThread, ce qui forcera une réinitialisation
  const _onLog = useCallback(({name, data}: {name: string; data?: Record<string, unknown>}) => {
    logger.debug(name, data);
  }, []);

  const _onError = useCallback((event: {error: Error}) => {
    logger.error('Erreur ChatKit : ', event.error);
  }, []);

  const {control, setThreadId: chatkitSetThreadId} = useChatKit({
    initialThread: threadId,
    // Interface du widget ChatKit ; les réponses de l'assistant, elles, suivent
    // `Accept-Language` (cf. `apiConfig`).
    locale: userLocale,
    theme: themeConfig,
    api: apiConfig,
    startScreen,
    header: {
      title: {
        enabled: true,
      },
      rightAction: {
        icon: 'grid',
        onClick: handleBackdropToggle,
      },
    },
    composer: {
      attachments: {enabled: false},
    },
    widgets: {onAction: handleAction},
    onClientTool: handleClientTool,
    onThreadChange: handleThreadChange,
    onThreadLoadEnd: ({threadId}) => logger.debug('Thread chargé : ', threadId),
    onThreadLoadStart: ({threadId}) => logger.debug('Chargement du thread : ', threadId),
    onResponseStart: () => logger.debug('Réponse en cours'),
    onResponseEnd: () => logger.debug('Réponse terminée'),
    onError: _onError,
    onLog: _onLog,
  });

  // Effet pour changer de thread quand l'extension le demande
  useEffect(() => {
    if (chatkitSetThreadId && threadId !== undefined) {
      logger.debug('Extension requested thread change, calling setThreadId:', threadId);
      chatkitSetThreadId(threadId);
    }
  }, [chatkitSetThreadId, threadId]);

  // Fonction pour recharger la page
  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  // Fonction pour ouvrir le support
  const handleGetSupport = useCallback(() => {
    const supportMessage = t('chatkit.error.crispMessage');
    crisp.openChat(supportMessage);
  }, [crisp, t]);

  // Affichage conditionnel : erreur, chargement, ou contenu normal
  if (errorState.type) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: darkMode ? '#1a1a1a' : '#e0ebee',
          color: darkMode ? '#fff' : '#000',
          padding: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" gutterBottom sx={{color: '#f44336', fontWeight: 'bold'}}>
          {errorState.type === 'invalid_origin' && t('chatkit.error.titleInvalidOrigin')}
          {errorState.type === 'timeout' && t('chatkit.error.titleTimeout')}
          {errorState.type === 'connection_failed' && t('chatkit.error.titleConnectionFailed')}
        </Typography>

        <Typography variant="body1" sx={{maxWidth: 500, marginBottom: 3, lineHeight: 1.6}}>
          {errorState.details}
        </Typography>

        {isDevelopment && errorState.origin && (
          <Typography
            variant="caption"
            sx={{
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              padding: 1,
              borderRadius: 1,
              marginBottom: 2,
              fontFamily: 'monospace',
            }}
          >
            Debug: {errorState.origin}
          </Typography>
        )}

        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={handleReload} sx={{backgroundColor: '#0a66c2'}}>
            {t('chatkit.error.reload')}
          </Button>
          <Button variant="outlined" onClick={handleGetSupport}>
            {t('chatkit.error.support')}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!isReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: darkMode ? '#1a1a1a' : '#e0ebee',
          color: darkMode ? '#fff' : '#000',
        }}
      >
        {t('global.loading')}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: darkMode ? '#1a1a1a' : '#e0ebee',
        position: 'relative',
      }}
    >
      <ChatKit control={control} style={{flexGrow: 1}} />

      {/* Backdrop avec menu d'actions */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        open={showBackdrop}
        onClick={handleBackdropClose}
      >
        <Stack
          spacing={2}
          alignItems="center"
          onClick={(e) => e.stopPropagation()} // Empêcher la fermeture au clic sur le menu
          sx={{
            backgroundColor: darkMode ? 'rgba(45, 45, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            padding: 3,
            borderRadius: 2,
            minWidth: 200,
            color: darkMode ? '#fff' : '#000',
          }}
        >
          <Typography variant="h6" sx={{color: darkMode ? '#fff' : '#000'}}>
            {t('chatkit.menu.title')}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={handleContactSupport}
            sx={{
              backgroundColor: '#0a66c2',
              '&:hover': {
                backgroundColor: '#085299',
              },
            }}
          >
            {t('chatkit.menu.contactSupport')}
          </Button>

          <Button variant="outlined" fullWidth onClick={handleBackdropClose}>
            {t('chatkit.menu.cancel')}
          </Button>
        </Stack>
      </Backdrop>
    </Box>
  );
}
